import React, { useEffect, useRef, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Grid, ToggleButton, ToggleButtonGroup, Typography, useTheme } from '@mui/material';
import {
  COLOR_CARNATION,
  COLOR_DENIM,
  COLOR_GALLERY,
  FONT_SIZE_16_PX,
  FONT_SIZE_39_PX,
  FONT_SIZE_49_PX,
  FONT_SIZE_61_PX,
} from 'popTheme';
import { ChangePassword } from './ChangePassword';
import { SettingsFormLabel } from './SettingsFormLabel';
import { PopInputField } from '../form/PopInputField';
import { ProvinceSelect } from '../form/ProvinceSelect';
import { YearSelect } from '../form/YearSelect';
import { LoadingBackdrop } from 'components/home/LoadingBackdrop';
import { selectProfile, selectProfileFailure } from 'redux/reducers/session';
import { sendClientAnalytics } from 'redux/sagas/clientAnalyticsSaga';
import { sendGetProfile } from 'redux/sagas/getProfileSaga';
import { sendUpdateProfile } from 'redux/sagas/updateProfileSaga';
import { AuditEventAction, createClientAnalytics } from '../../ClientAnalytics';
import PopPhoneInputField from '../form/PopPhoneInputField';
import Input from 'react-phone-number-input/input';
import { isPossiblePhoneNumber } from 'react-phone-number-input';

export const Settings = () => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const theme = useTheme();
  const ref = useRef();

  // Load the patient info.
  useEffect(() => {
    dispatch(sendGetProfile());
  }, [dispatch]);

  const profile = useSelector(selectProfile);
  const profileFailure = useSelector(selectProfileFailure);
  const sendingRequest = useSelector((state) => state.serverCommunication.sending);

  const [haveClearedStudyCode, setHaveClearedStudyCode] = useState(false);

  // We need a cache for the phone and nickname so we can submit the update only onBlur.
  const [formCache, setFormCache] = useState({
    nickname: '',
    phone: '',
    studyCode: '',
  });

  const hasStudyCodeError = () => {
    return !!profileFailure?.studyCode && !haveClearedStudyCode;
  };

  useEffect(() => {
    // If the study code is blank, it is possible that this is due to an error.
    // If it is an error, then use the cached code.  Otherwise use the studycode.
    const cachedStudyCode =
      (profile && profile.studyCode && profile.studyCode.trim() === '') || hasStudyCodeError()
        ? formCache.studyCode
        : profile?.studyCode || '';

    setFormCache({
      nickname: profile?.nickname || '',
      phone: profile?.phone || '',
      studyCode: cachedStudyCode,
    });
    setHaveClearedStudyCode(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile]);

  const messages = defineMessages({
    settingsTitle: {
      id: 'settings.title',
      defaultMessage: 'Settings',
      description: 'Settings page title text',
    },
    settingsBlurb: {
      id: 'settings.blurb',
      defaultMessage:
        'Please ensure that your profile is complete and up to date using the form below.',
      description: 'Settings page blurb',
    },
    settingsBlurb2: {
      id: 'settings.blurb2',
      defaultMessage: 'All fields marked with an asterisk (*) are mandatory.',
      description: 'Settings page blurb',
    },
    settingsNickname: {
      id: 'settings.nickname',
      defaultMessage: 'Nickname',
      description: 'Settings page nickname title',
    },
    settingsProvince: {
      id: 'settings.province',
      defaultMessage: 'Province or Territory',
      description: 'Settings page province or territory',
    },
    settingsEmail: {
      id: 'settings.email',
      defaultMessage: 'Email',
      description: 'Settings page email',
    },
    settingsAccessToken: {
      id: 'settings.access_token',
      defaultMessage: 'Access Token',
      description: 'Settings page access token',
    },
    settingsAccessTokenInvalid: {
      id: 'settings.access_token_invalid',
      defaultMessage: 'Access Token Invalid',
      description: 'Settings page access token invalid',
    },
    settingsYearOfBirth: {
      id: 'settings.year_of_birth',
      defaultMessage: 'Year of Birth',
      description: 'Settings page Year of Birth',
    },
    settingsCellPhoneNumber: {
      id: 'settings.cell',
      defaultMessage: 'Cell Phone Number',
      description: 'Settings page Cell Phone Number',
    },
    settingsReminders: {
      id: 'settings.reminders',
      defaultMessage: 'Reminders',
      description: 'Settings page Reminders',
    },
    settingsButtonEmail: {
      id: 'settings.button.email',
      defaultMessage: 'Email',
      description: 'Settings page email button',
    },
    settingsButtonText: {
      id: 'settings.button.text',
      defaultMessage: 'Text',
      description: 'Settings page Text button',
    },
    settingsLanguage: {
      id: 'settings.language',
      defaultMessage: 'Language',
      description: 'Settings page Language',
    },
    settingsButtonEnglish: {
      id: 'settings.button.english',
      defaultMessage: 'English',
      description: 'Settings page English button',
    },
    settingsButtonFrench: {
      id: 'settings.button.french',
      defaultMessage: 'French',
      description: 'Settings page French button',
    },
    settingsuiMode: {
      id: 'settings.light_dark_mode',
      defaultMessage: 'Light/Dark Mode',
      description: 'Settings page Light/Dark Mode',
    },
    settingsButtonLight: {
      id: 'settings.button.light',
      defaultMessage: 'Light',
      description: 'Settings page Light button',
    },
    settingsButtonDark: {
      id: 'settings.button.dark',
      defaultMessage: 'Dark',
      description: 'Settings page Dark button',
    },
    settingsPhoneNumberError: {
      id: 'phone.number.error.message',
      defaultMessage: 'Incorrect phone number format',
      description: 'Error text when phone number is incorrect',
    },
  });

  // Need these for the fields that don't immediately trigger a save.
  const handleCachedInputChange = (e) => {
    const { name, value } = e.target || e;
    if (name === 'studyCode' && value === '') {
      setHaveClearedStudyCode(true);
    }
    setFormCache((prevFormCache) => ({ ...prevFormCache, [name]: value }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target || e;

    let valueToStore = value;
    if (name === 'isEmailReminder' || name === 'isTextReminder') {
      // Got to reverse the setting for these ones.
      valueToStore = value === 'true' ? 'false' : 'true';
    } else if (name === 'nickname' && value?.trim() === '') {
      // If it's blank then we don't want to change anything.
      valueToStore = profile?.nickname;
      setFormCache((prevFormCache) => ({ ...prevFormCache, [name]: valueToStore }));
    }

    dispatch(sendUpdateProfile({ ...profile, [name]: valueToStore }));

    if (name === 'studyCode') {
      dispatch(
        sendClientAnalytics(
          createClientAnalytics(
            AuditEventAction.TOKEN_ENTERED.category,
            AuditEventAction.TOKEN_ENTERED.action,
            {
              token: value,
              email: profile?.email || '',
            },
          ),
        ),
      );
      setHaveClearedStudyCode(false);
    }
  };

  // We need this for the UI to make and asterisk red.s
  const SingleAsteriskRed = ({ text }) => {
    // Add a span for the asterisk.
    return (
      <>
        {text.substring(0, text.indexOf('*'))}
        <span style={{ color: COLOR_CARNATION }}>*</span>
        {text.substring(text.indexOf('*') + 1)}
      </>
    );
  };

  const languageButtons = [
    {
      value: 'ENGLISH',
      text: intl.formatMessage(messages.settingsButtonEnglish),
      testid: 'settings-english-button',
      name: 'language',
    },
    {
      value: 'FRENCH',
      text: intl.formatMessage(messages.settingsButtonFrench),
      testid: 'settings-french-button',
      name: 'language',
    },
  ];

  const uiModeButtons = [
    {
      value: 'LIGHT',
      text: intl.formatMessage(messages.settingsButtonLight),
      testid: 'settings-light-button',
      name: 'uiMode',
    },
    {
      value: 'DARK',
      text: intl.formatMessage(messages.settingsButtonDark),
      testid: 'settings-dark-button',
      name: 'uiMode',
    },
  ];

  const settingButtonCss = {
    height: '2.75rem',
    backgroundColor: COLOR_GALLERY,
    color: COLOR_DENIM,
    ':hover': {
      bgcolor: COLOR_GALLERY,
    },
    '&.Mui-selected, &.Mui-selected:hover': {
      color: COLOR_GALLERY,
      backgroundColor: COLOR_DENIM,
    },
    [theme.breakpoints.only('xs')]: {
      height: '2.25rem',
    },
  };

  const isValidPhoneNumber = formCache.phone.length === 0 || isPossiblePhoneNumber(formCache.phone);

  const SettingsToggleGroup = ({ testid, onChangeHandler, groupValue, groupButtons }) => {
    return (
      <ToggleButtonGroup
        fullWidth={true}
        exclusive
        onChange={onChangeHandler}
        value={groupValue}
        data-testid={testid}
      >
        {groupButtons.map((button) => (
          <ToggleButton
            key={button.value}
            value={button.value}
            name={button.name}
            sx={settingButtonCss}
            data-testid={button.testid}
          >
            {button.text}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
    );
  };

  return (
    <Box
      direction={'column'}
      px={{ xs: '0.75rem', sm: '3.75rem' }}
      py={{ xs: '0.75rem', sm: '3.75rem' }}
      width="-webkit-fill-available"
    >
      <Typography
        variant="h1"
        sx={{
          color: 'text.primary',
          fontSize: { xs: FONT_SIZE_39_PX, md: FONT_SIZE_49_PX, lg: FONT_SIZE_61_PX },
        }}
      >
        {intl.formatMessage(messages.settingsTitle)}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          color: 'text.primary',
          fontSize: { xs: FONT_SIZE_16_PX, md: '1.25rem' },
          mt: { xs: '1.5rem', sm: '2.5rem' },
          mb: { xs: '1rem', sm: '0.5rem' },
        }}
      >
        {intl.formatMessage(messages.settingsBlurb)}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          color: 'text.primary',
          fontSize: { xs: FONT_SIZE_16_PX, md: '1.25rem' },
        }}
      >
        <SingleAsteriskRed text={intl.formatMessage(messages.settingsBlurb2)} />
      </Typography>
      <Grid container py={'2rem'}>
        {sendingRequest && <LoadingBackdrop />}
        <Grid item md={8}>
          <Grid
            container
            spacing={{ xs: 1.5, sm: 2 }}
            justifyContent={'space-between'}
            columns={{ xs: 6, sm: 12 }}
          >
            <Grid item xs={6}>
              <SettingsFormLabel
                subtitle={intl.formatMessage(messages.settingsNickname)}
                isRequired={true}
              />
              <PopInputField
                required
                fullWidth
                size="small"
                name="nickname"
                id="nickname"
                inputProps={{ 'data-testid': 'settings-nickname-input', maxLength: 128 }}
                value={formCache.nickname}
                onChange={handleCachedInputChange}
                onBlur={handleInputChange}
              />
            </Grid>
            <Grid item xs={6}>
              <SettingsFormLabel
                error={hasStudyCodeError()}
                subtitle={
                  hasStudyCodeError()
                    ? intl.formatMessage(messages.settingsAccessTokenInvalid)
                    : intl.formatMessage(messages.settingsAccessToken)
                }
              />
              <PopInputField
                required
                fullWidth
                size="small"
                name="studyCode"
                id="studyCode"
                inputProps={{ 'data-testid': 'settings-study-code-input' }}
                value={formCache.studyCode}
                onChange={handleCachedInputChange}
                onBlur={handleInputChange}
                disabled={profile?.studyCode?.trim() !== '' || false}
                error={hasStudyCodeError()}
                hidebutton="true"
              />
            </Grid>
            <Grid item xs={6}>
              <SettingsFormLabel subtitle={intl.formatMessage(messages.settingsProvince)} />
              <ProvinceSelect
                onChange={handleInputChange}
                value={profile?.province || ''}
                name="province"
                id="province"
                inputProps={{ 'data-testid': 'settings-province-input' }}
              />
            </Grid>
            <Grid item xs={6}>
              <SettingsFormLabel subtitle={intl.formatMessage(messages.settingsYearOfBirth)} />
              <YearSelect
                id="birthYear"
                name="birthYear"
                value={profile?.birthYear || ''}
                onChange={handleInputChange}
                inputProps={{ 'data-testid': 'settings-birth-year-input' }}
              />
            </Grid>
            <Grid item xs={6}>
              <SettingsFormLabel
                subtitle={intl.formatMessage(messages.settingsEmail)}
                isRequired={true}
              />
              <PopInputField
                required
                fullWidth
                size="small"
                name="email"
                id="email"
                inputProps={{ 'data-testid': 'settings-email-input' }}
                value={profile?.email || ''}
                disabled={true}
              />
            </Grid>
            <Grid item xs={6}>
              <SettingsFormLabel subtitle={intl.formatMessage(messages.settingsCellPhoneNumber)} />
              <Input
                country="US"
                inputComponent={PopPhoneInputField}
                onChange={(value) => handleCachedInputChange({ name: 'phone', value: value || '' })}
                onBlur={() =>
                  isValidPhoneNumber && handleInputChange({ name: 'phone', value: formCache.phone })
                }
                value={formCache.phone}
                id="phone"
                name="phone"
                data-testid="settings-phone-input"
                ref={ref}
              />
              {!isValidPhoneNumber && (
                <Typography variant="subtitle1" align="center" color={COLOR_CARNATION} py="0.5rem">
                  {intl.formatMessage(messages.settingsPhoneNumberError)}
                </Typography>
              )}
            </Grid>
            {profile?.studyCode !== null && profile?.studyCode?.trim() !== '' && (
              <Grid item xs={6}>
                <SettingsFormLabel subtitle={intl.formatMessage(messages.settingsReminders)} />
                <Grid container spacing={{ xs: 1, sm: 4 }} justifyContent={'space-between'}>
                  <Grid item xs={6}>
                    <ToggleButton
                      key={'isEmailReminder'}
                      name={'isEmailReminder'}
                      value={profile?.isEmailReminder || false}
                      disableFocusRipple
                      disableRipple
                      fullWidth={true}
                      selected={profile.isEmailReminder === 'true'}
                      onChange={handleInputChange}
                      sx={settingButtonCss}
                      data-testid="settings-email-reminder-button"
                    >
                      {intl.formatMessage(messages.settingsButtonEmail)}
                    </ToggleButton>
                  </Grid>
                  <Grid item xs={6}>
                    <ToggleButton
                      key={'isTextReminder'}
                      name={'isTextReminder'}
                      value={profile?.isTextReminder || false}
                      disableFocusRipple
                      disableRipple
                      fullWidth={true}
                      selected={profile.isTextReminder === 'true'}
                      onChange={handleInputChange}
                      sx={settingButtonCss}
                      data-testid="settings-text-reminder-button"
                    >
                      {intl.formatMessage(messages.settingsButtonText)}
                    </ToggleButton>
                  </Grid>
                </Grid>
              </Grid>
            )}
            <Grid item xs={6}>
              <SettingsFormLabel subtitle={intl.formatMessage(messages.settingsLanguage)} />
              <SettingsToggleGroup
                testid={'settings-language-group'}
                onChangeHandler={handleInputChange}
                groupValue={profile?.language || 'ENGLISH'}
                groupButtons={languageButtons}
              />
            </Grid>
            <Grid item xs={6}>
              <SettingsFormLabel subtitle={intl.formatMessage(messages.settingsuiMode)} />
              <SettingsToggleGroup
                testid={'settings-light-dark-mode-group'}
                onChangeHandler={handleInputChange}
                groupValue={profile?.uiMode || 'LIGHT'}
                groupButtons={uiModeButtons}
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid item md={4} pl={{ xs: '0', sm: '2rem' }}>
          <ChangePassword />
        </Grid>
      </Grid>
    </Box>
  );
};
